import { useState } from 'react'
import {
  Form,
  InputNumber,
  Row,
  Col,
  Radio,
  Button,
  Select,
  RadioChangeEvent,
  Switch,
  Space,
  Tooltip,
  Card,
  Tag
} from 'antd'
import { useTranslation } from 'react-i18next'
import type { OrderRequestProps, OrderItemProps, IItem, RelatedProductItem as IRelatedProducts } from '@/types/IOrder'
import IconButton from '@components/common/IconButton'
import Icon from '@components/common/Icon'
import { useRelatedProducts } from '@hooks/useRelatedProducts'
import { useProductsList } from '@hooks/useProductsList'
import { ButtonRadioInput } from '@components/common/ButtonRadioInput'
import SliderItem from '../SliderItem'

import Title from '@components/common/Title'

const FormItemsOrderRequest = ({ name, form }: OrderRequestProps) => {
  const data = form.getFieldsValue()
  const relatedProducts = data?.items?.[name]?.order_request?.related_products ?? data?.items?.[name]?.product?.related_products ?? []
  const supportLevel = data?.items?.[name]?.order_request?.support_level ?? "BASIC";
  const { t } = useTranslation()

  return (
    <>
      <ButtonRadioInput
        initialValue={supportLevel}
        inputName={[name, 'support_level']}
        constant="PRODUCT_SUPPORT_LEVEL"
        label="PRODUCT_SUPPORT_LEVEL"
        required
        span={24}
        buttonStyle="solid"
        size="large"
        data-cy="support-level-radio"
      />

      <Form.List name={[name, 'order_request', 'items']}>
        {fields => {
          return (
            <>
              {fields.map(({ name: orderRequestKey }) => (
                <div key={orderRequestKey}>
                  {data?.items ? (
                    <OrderItem
                      orderRequestKey={orderRequestKey}
                      data={data.items[name].order_request.items[orderRequestKey]}
                      parentName={name}
                    />
                  ) : null}
                </div>
              ))}
            </>
          )
        }}
      </Form.List>

      {relatedProducts?.length > 0 && (
        <>
          <Title>{t('RelatedProducts')}</Title>
          <RelatedProducts
            data={data}
            parentName={name}
            form={form}
          />
        </>
      )}
    </>
  )
}

const OrderItem = ({ orderRequestKey, data, parentName }: OrderItemProps & { parentName?: number }) => {
  return (
    <Row gutter={[8, 8]}>
      <Col span={24}>
        {data.item.children?.length > 0 && (
          <ChildrenItem data={data} orderRequestKey={orderRequestKey} />
        )}

        {data.item.type === 'BOOL' && (
          <SwitchItem data={data} formName={[orderRequestKey, 'is_used']} />
        )}
        {data.item.type === 'ITEM' && (
          <SliderItem
            formName={[orderRequestKey, 'current']}
            data={data}
            parentName={parentName}
            currentProductData={data}
          />
        )}
      </Col>
    </Row>
  )
}

const SwitchItem = ({ formName, data }: any) => {
  return (
    <Col span={24}>
      <Form.Item
        name={formName}
        label={
          <Space>
            <Icon name={`fa-light ${data.item.icon_name}`} />
            <Tooltip title={data.item.description}>
              <span>{data.item.override_name ?? data.item.item}</span>
            </Tooltip>
          </Space>
        }
        required={data.required}
      >
        <Switch />
      </Form.Item>
    </Col>
  )
}

const ChildrenItem = ({ data, orderRequestKey }: OrderItemProps) => {
  const { t } = useTranslation()
  const [selectedChildren, setSelectedChildren] = useState<IItem[]>(
    (data?.children?.items && Array.isArray(data.children.items))
      ? data.children.items.map(item => item?.item)
      : []
  )
  const handleRadioChange = (e: RadioChangeEvent, name: number) => {
    const value = e.target.value
    const selectedChildItem = data.item.children.find(
      (child: IItem) => child.uuid === value
    )
    const newSelectedChildren = [...selectedChildren]
    newSelectedChildren[name] = selectedChildItem
    setSelectedChildren(newSelectedChildren)
  }
  return (
    <>
      {data.item.type === 'LIST' ? (
        <>
          <span>{data.item.item}</span>
          <Form.List name={[orderRequestKey, 'children', 'items']}>
            {(fields, { add, remove }) => (
              <>
                <Row gutter={[8, 8]} style={{ marginBottom: '10px' }}>
                  {fields.map(({ key, name }) => (
                    <Col key={key} span={24}>
                      <Row gutter={[16, 16]}>
                        <>
                          <Form.Item name={[name, 'item', 'uuid']}>
                            <Radio.Group
                              optionType="button"
                              buttonStyle="solid"
                              onChange={e => handleRadioChange(e, name)}
                              style={{ marginRight: '20px' }}
                            >
                              {data.item.children.map((children: IItem) => (
                                <Radio key={children.uuid} value={children.uuid}>
                                  {children.item}
                                </Radio>
                              ))}
                            </Radio.Group>
                          </Form.Item>
                          <Form.Item name={[name, 'current']}>
                            <InputNumber
                              addonAfter={selectedChildren[name]?.postfix}
                              style={{ marginRight: '20px' }}
                              min={selectedChildren[name]?.min}
                              max={selectedChildren[name]?.max}
                            />
                          </Form.Item>
                        </>
                        <IconButton
                          name="fa-light fa-trash"
                          onClick={() => remove(name)}
                          data-cy={`delete-item-${name}`}
                        />
                      </Row>
                    </Col>
                  ))}
                </Row>
                <Form.Item>
                  <Button
                    data-cy="add-item"
                    type="dashed"
                    onClick={() => add()}
                    block
                  >
                    {t('ADD_ITEM')}
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </>
      ) : (
        <Form.Item
          name={[orderRequestKey, 'children', 'items', 0, 'item', 'uuid']}
          label={data.item.override_name ?? data.item.item}
          required={data.item.required}
        >
          <Select
            showSearch
            allowClear
            optionFilterProp="label"
            filterOption={(input, option) =>
              option.label.toLowerCase().includes(input.toLowerCase())
            }
            options={data.item.children.map((item: IItem) => ({
              value: item.uuid,
              label: item.override_name ?? item.item,
            }))}
          />
        </Form.Item>
      )}
    </>
  )
}

export const RelatedProducts = ({
  data,
  parentName,
  form,
}: {
  data: IRelatedProducts[]
  parentName: number
  form?: any
}) => {
  const { t } = useTranslation()
  const {
    relatedProducts,
    filteredProducts,
  } = useRelatedProducts({ data, parentName, form })

  if (!data || data.length === 0) return null
  if (!relatedProducts || relatedProducts.length === 0) return null

  return (
    <ProductsList
      data={data}
      parentName={parentName}
      filteredProducts={filteredProducts}
      t={t}
      form={form}
    />
  )
}

const ProductsList = ({ data, parentName, filteredProducts, t, form }: {
  data: any,
  parentName: number,
  filteredProducts: any[],
  t: (key: string) => string,
  form: any
}) => {
  const { getProductCountMap, shouldRenderProduct } = useProductsList({
    filteredProducts,
    parentName,
    form
  });

  return (
    <Form.List name={[parentName, 'order_request', 'related_products']}>
      {(fields) => {
        const productCountMap = getProductCountMap(fields);

        return (
          <Row gutter={[16, 16]}>
            {fields.map(({ name: relatedProductsKey, ...field }) => {
              const fieldValue = form.getFieldValue(
                ['items', parentName, 'order_request', 'related_products', relatedProductsKey]
              );

              if (!shouldRenderProduct(fieldValue, productCountMap, filteredProducts)) {
                return null;
              }

              return (
                <ProductCard
                  key={field.key}
                  data={data}
                  parentName={parentName}
                  relatedProductsKey={relatedProductsKey}
                  filteredProducts={filteredProducts}
                  t={t}
                />
              );
            })}
          </Row>
        );
      }}
    </Form.List>
  );
};

const ProductCard = ({ data, parentName, relatedProductsKey, filteredProducts, t }: {
  data: IRelatedProducts[],
  parentName: number,
  relatedProductsKey: number,
  filteredProducts: any[],
  t: (key: string) => string
}) => {
  const currentProductInForm = data?.items?.[parentName]?.order_request?.related_products?.[relatedProductsKey]
  if (!currentProductInForm) return null

  const productUuid = currentProductInForm.product?.uuid ?? currentProductInForm.uuid
  if (!productUuid) return null

  const originalProductData = filteredProducts.find(
    (product) => (product.product?.uuid === productUuid || product.uuid === productUuid)
  )

  if (!originalProductData) return null

  const productName = currentProductInForm.name ?? currentProductInForm.product?.name
  const supportLevel = currentProductInForm.pivot?.support_level ?? currentProductInForm.product?.pivot?.support_level

  const title = (
    <Space>
      <span>{productName}</span>
      <Tag color="blue">
        {t(supportLevel)}
      </Tag>
    </Space>
  );

  return (
    <Col xs={24} key={relatedProductsKey}>
      <Form.Item noStyle>
        <Card
          title={title}
          size="small"
          data-cy={`related-product-${relatedProductsKey}`}
          style={{ marginBottom: 16 }}
        >
          {currentProductInForm.items && currentProductInForm.items.length > 0 && (
            <ProductItems
              items={currentProductInForm.items}
              relatedProductsKey={relatedProductsKey}
            />
          )}
        </Card>
      </Form.Item>
    </Col>
  )
}

const ProductItems = ({ items, relatedProductsKey }: {
  items: any[],
  relatedProductsKey: number
}) => (
  <Form.List name={[relatedProductsKey, 'items']}>
    {(fields) => (
      <Row gutter={[8, 8]}>
        {fields.map(({ name: itemField }) => (
          <Col span={24} key={itemField}>
            <OrderItem
              orderRequestKey={itemField}
              data={items[itemField]}
            />
          </Col>
        ))}
      </Row>
    )}
  </Form.List>
)

export default FormItemsOrderRequest
